import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, apiGet, apiSend } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useAuthDialog } from '../auth/AuthDialogContext';
import '../styles/vip-cashback.css';

type VipLevel = {
  level: number;
  name: string;
  cashback_bps: number;
  claim_days: number;
  xp_per_azn: number;
  xp_to_next: number;
};

type VipCatalog = {
  mode: string;
  levels: VipLevel[];
  max_category_bps: Record<string, number>;
};

type VipStatus = VipCatalog & {
  level: number;
  level_name: string;
  xp: number;
  xp_to_next: number;
  is_max: boolean;
  cashback_bps: number;
  claim_days: number;
  claimable_minor: number;
  claim: {
    amount_minor: number;
    interval_blocked: boolean;
    next_claim_at: string | null;
    mode: string;
    min_claim_minor: number;
  };
  pending_level_up: number | null;
  first_claim_done: boolean;
};

type History = {
  claims: Array<{ id: number; amount_minor: number; level: number; created_at: string | null }>;
  level_ups: Array<{ id: number; from_level: number; to_level: number; created_at: string | null }>;
};

const FALLBACK_LEVELS: VipLevel[] = [
  { level: 1, name: 'Mis', cashback_bps: 500, claim_days: 7, xp_per_azn: 100, xp_to_next: 300_000 },
  { level: 2, name: 'Bürünc', cashback_bps: 600, claim_days: 6, xp_per_azn: 150, xp_to_next: 700_000 },
  { level: 3, name: 'Gümüş', cashback_bps: 700, claim_days: 5, xp_per_azn: 200, xp_to_next: 1_000_000 },
  { level: 4, name: 'Qızıl', cashback_bps: 800, claim_days: 4, xp_per_azn: 250, xp_to_next: 3_000_000 },
  { level: 5, name: 'Yaqut', cashback_bps: 900, claim_days: 3, xp_per_azn: 300, xp_to_next: 10_000_000 },
  { level: 6, name: 'Safir', cashback_bps: 1000, claim_days: 2, xp_per_azn: 350, xp_to_next: 10_000_000 },
  { level: 7, name: 'Almaz', cashback_bps: 1100, claim_days: 1, xp_per_azn: 400, xp_to_next: 50_000_000 },
  { level: 8, name: 'VIP MAX', cashback_bps: 0, claim_days: 1, xp_per_azn: 0, xp_to_next: 0 },
];

const FALLBACK_MAX = { slot: 25, lottery: 20, live: 10, table: 5 };

const EMBLEMS: Record<number, string> = {
  1: '/vip-emblems/1.jpg',
  2: '/vip-emblems/2.jpg',
  3: '/vip-emblems/3.jpg',
  4: '/vip-emblems/4.jpg',
  5: '/vip-emblems/5.jpg',
  6: '/vip-emblems/6.jpg',
  7: '/vip-emblems/7.jpg',
  8: '/vip-emblems/8.jpg',
};

function azn(minor: number) {
  return (minor / 100).toFixed(2);
}

function fmtXp(n: number, locale: string) {
  return Math.max(0, Math.floor(n)).toLocaleString(locale).replace(/,/g, ' ');
}

export function VipPage() {
  const { t, i18n } = useTranslation(['vip', 'common']);
  const { user } = useAuth();
  const { openAuth } = useAuthDialog();
  const [vip, setVip] = useState<VipStatus | null>(null);
  const [catalog, setCatalog] = useState<VipCatalog | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [slide, setSlide] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [levelUp, setLevelUp] = useState<number | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const locale = (i18n.resolvedLanguage || i18n.language || 'az').slice(0, 2);

  const loadCatalog = useCallback(async () => {
    try {
      setCatalog(await apiGet<VipCatalog>('/api/v1/vip-cashback/catalog'));
    } catch {
      setCatalog({ mode: 'active', levels: FALLBACK_LEVELS, max_category_bps: FALLBACK_MAX });
    }
  }, []);

  const load = useCallback(async () => {
    if (!user) {
      setVip(null);
      setHistory(null);
      return;
    }
    const st = await apiGet<VipStatus>('/api/v1/vip-cashback/status');
    setVip(st);
    setSlide(Math.max(0, Math.min(7, (st.level || 1) - 1)));
    if (st.pending_level_up) setLevelUp(st.pending_level_up);
    setHistory(await apiGet<History>('/api/v1/vip-cashback/history'));
  }, [user]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    void load().catch(() => setVip(null));
  }, [load]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.children[slide] as HTMLElement | undefined;
    card?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [slide]);

  const levels = vip?.levels?.length ? vip.levels : catalog?.levels?.length ? catalog.levels : FALLBACK_LEVELS;
  const maxCategory = vip?.max_category_bps || catalog?.max_category_bps || FALLBACK_MAX;
  const currentLevel = user && vip ? vip.level : 0;
  const mode = vip?.mode || catalog?.mode || 'active';

  const claimDisabled =
    !vip ||
    vip.mode !== 'active' ||
    vip.claim.interval_blocked ||
    vip.claimable_minor < (vip.claim.min_claim_minor || 100) ||
    busy;

  function levelName(lvl: VipLevel) {
    return t(`vip:level_${lvl.level}`, { defaultValue: lvl.name });
  }

  function intervalLabel(days: number) {
    return days <= 1 ? t('vip:daily') : t('vip:everyDays', { days });
  }

  function cashbackTitle(lvl: VipLevel) {
    if (lvl.level === 8) {
      const vals = [Number(maxCategory.table || 5), Number(maxCategory.live || 10), Number(maxCategory.lottery || 20), Number(maxCategory.slot || 25)];
      const lo = (Math.min(...vals) / 100).toFixed(2);
      const hi = (Math.max(...vals) / 100).toFixed(2);
      return t('vip:cashbackMax', { lo, hi });
    }
    return t('vip:cashback', { pct: (lvl.cashback_bps / 100).toFixed(0) });
  }

  async function doClaim() {
    if (!vip) return;
    setBusy(true);
    setErr(null);
    try {
      await apiSend('POST', '/api/v1/vip-cashback/claim', {
        idempotency_key: `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      });
      setConfirmOpen(false);
      await load();
    } catch (e) {
      const detail =
        e instanceof ApiError
          ? (e.body as { detail?: { message?: string; code?: string; missing_minor?: number; next_claim_at?: string } })?.detail
          : null;
      if (detail?.message) setErr(detail.message);
      else setErr('Claim failed');
    } finally {
      setBusy(false);
    }
  }

  async function dismissLevelUp() {
    if (!levelUp) return;
    try {
      await apiSend('POST', '/api/v1/vip-cashback/ack-level-up', {});
    } catch {
      /* ignore */
    }
    setLevelUp(null);
    await load();
  }

  return (
    <div className={`vipx-page${mode !== 'active' ? ' is-paused' : ''}`}>
      <header className="vipx-top">
        <Link to="/" className="vipx-back" aria-label="Back">
          ←
        </Link>
        <h1>{t('vip:title').toUpperCase()}</h1>
        <button type="button" className="vipx-info" aria-label="Info" onClick={() => setRulesOpen(true)}>
          i
        </button>
      </header>

      <section className="vipx-carousel" aria-label="VIP">
        <div
          className="vipx-track"
          ref={trackRef}
          onScroll={() => {
            const el = trackRef.current;
            if (!el) return;
            const w = el.clientWidth;
            if (!w) return;
            const idx = Math.round(el.scrollLeft / Math.max(1, w * 0.86));
            setSlide(Math.max(0, Math.min(levels.length - 1, idx)));
          }}
        >
          {levels.map((lvl) => {
            const done = Boolean(user && currentLevel > 0 && lvl.level < currentLevel);
            const current = Boolean(user && currentLevel > 0 && lvl.level === currentLevel);
            const locked = Boolean(user && currentLevel > 0 && lvl.level > currentLevel) || (!user && lvl.level === 8);
            const preview = !user;
            const isMax = lvl.level === 8;
            const need = lvl.xp_to_next || 0;
            let xpNow = 0;
            let pct = 0;
            if (done) {
              xpNow = need;
              pct = 100;
            } else if (current && vip) {
              xpNow = isMax ? 0 : vip.xp;
              pct = isMax ? 100 : need ? Math.max(0, Math.min(100, (vip.xp / need) * 100)) : 100;
            } else if (preview && !isMax) {
              xpNow = 0;
              pct = 0;
            }

            let status = '';
            if (done) status = t('vip:completed');
            else if (current) status = isMax ? 'VIP MAX' : t('vip:yourLevel');
            else if (locked || isMax) status = t('vip:lockedShort', { defaultValue: t('vip:locked') });

            return (
              <article
                key={lvl.level}
                className={`vipx-slide vipx-tone-${lvl.level}${current ? ' is-current' : ''}${locked ? ' is-locked' : ''}${done ? ' is-done' : ''}`}
              >
                <img className="vipx-emblem-img" src={EMBLEMS[lvl.level]} alt="" />
                <h2>{levelName(lvl).toUpperCase()}</h2>
                <p className="vipx-status">{status || '\u00a0'}</p>

                {locked && !done ? (
                  <p className="vipx-lock-note">{t('vip:lockedNote', { name: levelName(lvl) })}</p>
                ) : null}

                {!locked || done || current ? (
                  <>
                    {!isMax ? (
                      <>
                        <div className="vipx-xp-row">
                          <span>
                            {t('vip:xpLabel')}: {fmtXp(done || current ? xpNow : 0, locale)}
                          </span>
                          <span>{fmtXp(need, locale)}</span>
                        </div>
                        <div className="vipx-bar">
                          <div style={{ width: `${pct}%` }} />
                        </div>
                        <p className="vipx-rule">{t('vip:xpRule', { xp: lvl.xp_per_azn })}</p>
                      </>
                    ) : current || preview ? (
                      <p className="vipx-rule">{t('vip:maxNote')}</p>
                    ) : null}
                  </>
                ) : null}

                <div className={`vipx-cb-box${current ? ' has-claim' : ''}`}>
                  <div className="vipx-cb-left">
                    <span className="vipx-bag" aria-hidden="true">
                      💰
                    </span>
                    <div>
                      <strong>{cashbackTitle(lvl)}</strong>
                      <small>{intervalLabel(lvl.claim_days)}</small>
                    </div>
                  </div>

                  {current && user && vip ? (
                    <div className="vipx-cb-right">
                      <span>{t('vip:active', { amount: azn(vip.claimable_minor) })}</span>
                      <button type="button" className="vipx-claim" disabled={claimDisabled} onClick={() => setConfirmOpen(true)}>
                        {t('vip:claim')}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>

        <div className="vipx-dots" role="tablist">
          {levels.map((lvl, i) => (
            <button key={lvl.level} type="button" className={i === slide ? 'is-on' : undefined} aria-label={levelName(lvl)} onClick={() => setSlide(i)} />
          ))}
        </div>
      </section>

      {!user ? (
        <div className="vipx-login">
          <p>{t('vip:loginHint')}</p>
          <button type="button" className="vipx-claim" onClick={() => openAuth('login')}>
            {t('vip:login')}
          </button>
        </div>
      ) : null}

      {err ? <p className="vipx-err">{err}</p> : null}
      {mode === 'maintenance' ? <p className="vipx-err">{t('vip:maintenance')}</p> : null}

      <section className="vipx-how">
        <h3>{t('vip:howTitle')}</h3>
        <div className="vipx-how-row">
          {[
            { src: '/vip-emblems/how-coin.jpg', text: t('vip:how1') },
            { src: '/vip-emblems/how-star.jpg', text: t('vip:how2') },
            { src: '/vip-emblems/how-gift.jpg', text: t('vip:how3') },
          ].map((s, i, arr) => (
            <div key={s.text} className="vipx-how-step">
              <img className="vipx-how-icon-img" src={s.src} alt="" />
              <p>{s.text}</p>
              {i < arr.length - 1 ? <span className="vipx-how-arrow">››</span> : null}
            </div>
          ))}
        </div>
      </section>

      {user ? (
        <section className="vipx-history">
          <h3>{t('vip:history')}</h3>
          {(history?.claims || []).length === 0 && (history?.level_ups || []).length === 0 ? (
            <p className="vipx-muted">{t('vip:historyEmpty')}</p>
          ) : null}
          {(history?.claims || []).map((c) => (
            <article key={`c-${c.id}`}>
              <strong>
                {t('vip:claimTitle')}: {azn(c.amount_minor)} AZN
              </strong>
              <span>
                L{c.level} · {c.created_at || '—'}
              </span>
            </article>
          ))}
          {(history?.level_ups || []).map((u) => (
            <article key={`u-${u.id}`}>
              <strong>
                L{u.from_level} → L{u.to_level}
              </strong>
              <span>{u.created_at || '—'}</span>
            </article>
          ))}
        </section>
      ) : null}

      {rulesOpen ? (
        <div className="vipx-modal-bg vipx-rules-bg" role="dialog">
          <div className="vipx-rules">
            <header>
              <h3>{t('vip:rulesTitle')}</h3>
              <button type="button" onClick={() => setRulesOpen(false)} aria-label="close">
                ×
              </button>
            </header>
            <div className="vipx-rules-body">
              <p>{t('vip:rulesIntro')}</p>
              <h4>{t('vip:rulesWhatTitle')}</h4>
              <p>{t('vip:rulesWhat')}</p>
              <h4>{t('vip:rulesLevelsTitle')}</h4>
              <ol>
                <li>{t('vip:rulesLevels1')}</li>
                <li>{t('vip:rulesLevels2')}</li>
              </ol>
              <h4>{t('vip:rulesXpTitle')}</h4>
              <p>{t('vip:rulesXpNote')}</p>
              <ul>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <li key={i}>{t(`vip:rulesXp${i}`)}</li>
                ))}
              </ul>
              <h4>{t('vip:rulesCbTitle')}</h4>
              <ol>
                <li>{t('vip:rulesCb1')}</li>
                <li>
                  <code>{t('vip:rulesCbFormula')}</code>
                </li>
                <li>{t('vip:rulesCb2')}</li>
                <li>{t('vip:rulesCb3')}</li>
              </ol>
            </div>
          </div>
        </div>
      ) : null}

      {confirmOpen && vip ? (
        <div className="vipx-modal-bg" role="dialog">
          <div className="vipx-modal">
            <h3>{t('vip:claimTitle')}</h3>
            <p>{t('vip:claimAmount', { amount: azn(vip.claimable_minor) })}</p>
            <p className="vipx-muted">{t('vip:claimNote')}</p>
            <div className="vipx-modal-actions">
              <button type="button" onClick={() => setConfirmOpen(false)}>
                {t('vip:cancel')}
              </button>
              <button type="button" className="primary" disabled={busy} onClick={() => void doClaim()}>
                {t('vip:confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {levelUp && vip ? (
        <div className="vipx-modal-bg" role="dialog">
          <div className="vipx-modal">
            <h3>{t('vip:congrats')}</h3>
            <p>{t('vip:newLevel', { name: t(`vip:level_${levelUp}`) })}</p>
            <div className="vipx-modal-actions">
              <button type="button" className="primary" onClick={() => void dismissLevelUp()}>
                {t('vip:continue')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
